// =============================================================================
// FILE: packages/renderer/src/stores/auth.store.ts
// =============================================================================

import { create } from 'zustand';
import { tokens } from '../lib/api.js';

export interface SessionUser {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  permissionCache: string[];
  provider: string;
  expiresAt: string;
  role?: string;
  mustChangePassword?: boolean;
  name?: string;
  voicePreference?: 'Bella' | 'Roger' | 'Text Only';
}

interface AuthState {
  user: SessionUser | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithEntra: () => Promise<void>;
  setEntraSession: (payload: LoginResponse) => void;
  logout: () => Promise<void>;
  hydrate: () => Promise<void>;
  updateProfile: (payload: { name?: string; voicePreference?: 'Bella' | 'Roger' | 'Text Only' }) => Promise<void>;
}

interface LoginResponse {
  session: SessionUser;
  tokens: { accessToken: string; refreshToken: string; expiresAt: string };
  user: { id: string; email: string; name?: string; role?: string; mustChangePassword?: boolean };
}

const API = (import.meta as any).env?.VITE_BACKEND_URL ?? '';
const api = (path: string) => `${API}${path}`;

function formatApiErrorDetail(detail: unknown): string {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e === 'object' && 'msg' in e ? String((e as { msg: unknown }).msg) : JSON.stringify(e))).join(' ');
  if (typeof detail === 'object' && 'msg' in detail) return String((detail as { msg: unknown }).msg);
  return String(detail);
}

async function authFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const access = tokens.get();
  const res = await fetch(api(path), {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(access ? { Authorization: `Bearer ${access}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(formatApiErrorDetail((data as { detail?: unknown }).detail));
  return data as T;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: false,
  error: null,

  async login(email, password) {
    set({ loading: true, error: null });
    try {
      const r = await authFetch<LoginResponse>('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
      tokens.set(r.tokens.accessToken, r.tokens.refreshToken);
      set({ user: r.session, loading: false, error: null });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Login failed';
      set({ loading: false, error: msg, user: null });
    }
  },

  async logout() {
    try { await authFetch('/api/auth/logout', { method: 'POST' }); } catch { /* ignore */ }
    tokens.clear();
    set({ user: null });
  },

  /** Starts the Microsoft Entra ID device-code flow. The challenge is delivered
   *  via the push channel `PUSH_ENTRA_DEVICE_CODE`; subscribers can show the
   *  verification URI + user code. Final session arrives via `AUTH_ENTRA_DEVICE_AWAIT`. */
  async loginWithEntra() {
    set({ loading: false, error: 'Microsoft Entra sign-in is disabled in production auth mode.' });
  },

  setEntraSession(payload: LoginResponse) {
    tokens.set(payload.tokens.accessToken, payload.tokens.refreshToken);
    set({ user: payload.session, loading: false, error: null });
  },

  async hydrate() {
    if (!tokens.get()) {
      set({ user: null });
      return;
    }
    try {
      const r = await authFetch<LoginResponse>('/api/auth/me');
      if (r.tokens?.accessToken) tokens.set(r.tokens.accessToken, r.tokens.refreshToken);
      set({ user: r.session });
    } catch {
      tokens.clear();
      set({ user: null });
    }
  },

  async updateProfile(payload) {
    const body: Record<string, string> = {};
    if (payload.name !== undefined) body.name = payload.name;
    if (payload.voicePreference !== undefined) body.voice_preference = payload.voicePreference;
    const r = await authFetch<LoginResponse>('/api/auth/profile', { method: 'PATCH', body: JSON.stringify(body) });
    set({ user: r.session, error: null });
  },
}));
